import type { FC } from 'react';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { styles } from './index.styles';

const dataLabels = ['NotStarted', 'InProgress', 'Completed'];

interface ActionPlanStatusDoughnutProps {
  actionPlanCount: number;
  cumulatedActionPlanStatusCount: number[];
  bigText: boolean;
}

const ActionPlanStatusDoughnut: FC<ActionPlanStatusDoughnutProps> = ({
  actionPlanCount = 0,
  cumulatedActionPlanStatusCount = [0, 0, 0],
  bigText,
}) => {
  const { t } = useTranslation('planAction');
  const { colors } = useTheme();
  const backgroundColor = [
    colors.neutral[200],
    colors.secondary[100],
    colors.secondary[500],
  ];

  const labels = useMemo(
    () =>
      cumulatedActionPlanStatusCount.map(
        (count, index) =>
          `${t(`ActionPlan.Status.${dataLabels[index]}`)} (${Math.round(
            (100 * count) / actionPlanCount,
          )}%)`,
      ),
    [t, cumulatedActionPlanStatusCount, actionPlanCount],
  );

  return (
    <Stack direction="row" spacing={4}>
      <Box>
        <Doughnut
          width={200}
          height={200}
          data={{
            labels,
            datasets: [
              {
                data: cumulatedActionPlanStatusCount,
                backgroundColor,
              },
            ],
          }}
          options={{
            plugins: {
              legend: { display: false },
            },
            cutout: 50,
            layout: { padding: 1 },
          }}
        />
      </Box>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: bigText ? 22 : 18,
            pb: 2,
          }}
        >
          {t('Statistics.Coverage.ActionPlanStatus')}
        </Typography>
        {labels.map((label, index) => (
          <Stack
            direction="row"
            spacing={1}
            key={label}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box sx={styles.legendColorBox(backgroundColor[index])} />
            <Typography
              sx={{
                fontSize: bigText ? 18 : 13,
              }}
            >
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default ActionPlanStatusDoughnut;
